import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Avatar, Flex, Input, PaddingInset, useDebounce } from '@cash-design-system/react'
import { RewardCodeBlocker } from '@cash-web/protos/squareup/franklin/blockers/reward_code_blocker.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationApplyRewardCode, useQueryCheckRewardCode } from '@cash-web/shared-data-access-api/react-query'

import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
import { BlockerLoading } from '../BlockerLoading/BlockerLoading'

export const BlockerRewardCode = ({ blocker }: { blocker: RewardCodeBlocker }) => {
  const form = useForm({
    defaultValues: {
      code: blocker.suggested_code,
    },
  })

  const theme = useTheme()
  const { t } = useTranslation()
  const { mutateAsync: applyRewardCode } = useMutationApplyRewardCode()
  const code = form.watch('code')
  const debouncedCode = useDebounce(code, 300)

  const { data: checkRewardCode, isLoading: isLoadingCheckRewardCode } = useQueryCheckRewardCode({
    variables: { code: debouncedCode },
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  })

  const { handleBlockerResponse } = useBlockerResponseHandler()

  if (isLoadingCheckRewardCode && blocker.suggested_code === code) return <BlockerLoading />

  const mainText = checkRewardCode?.reward_text
    ? checkRewardCode.reward_text
    : t('Do you have a Cash Referral Code?', { context: 'main text for reward code blocker' })

  return (
    <Flex flexDirection="column" gap="l">
      <PaddingInset>
        <Avatar
          avatarData={{
            accent_color: theme.arcadeColors.semantic.backgroundBrand,
            image_url: checkRewardCode?.inviter_photo_url,
            icon: {
              arcade_id: '7pSmPI', // family icon
            },
          }}
          avatarType={checkRewardCode?.inviter_photo_url ? 'UserImage' : 'Icon'}
          size="medium"
        />
      </PaddingInset>
      <BaseBlocker
        mainText={mainText}
        form={form}
        skippable={true}
        onSubmit={async (data: { code: string }) => {
          await handleBlockerResponse({
            isResponseSuccess: response => response.valid === true,
            promisedBlockerUpdate: applyRewardCode({
              code: data.code,
            }),
          })
        }}
      >
        <Input
          placeholder="Enter your Cash Referral Code"
          defaultValue={blocker.suggested_code}
          minLength={blocker.minimum_code_length}
          {...form.register('code')}
        />
      </BaseBlocker>
    </Flex>
  )
}
